import { Component, LeasingProject } from '@/features/end-of-lease/types';
import { Clock } from 'lucide-react';

import { formatDate } from '@/lib/utils';

interface Props {
    project?: LeasingProject;
    components?: Component[];
    currentDate?: Date;
}

export function Summary({
    project,
    components = [],
    currentDate = new Date(),
}: Props) {
    // Calculate days until delivery
    const calculateDaysUntil = (dateString?: string | null) => {
        if (!dateString) return null;
        const targetDate = new Date(dateString);
        const today = currentDate;
        const diffTime = targetDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const daysUntilDelivery = calculateDaysUntil(project?.deliveryDate);

    // Extract aircraft details from project
    const rawMsn = project?.msn || project?.leasingAircraft?.msn || null;
    const msn = rawMsn || '-';
    const registration = project?.leasingAircraft?.registration || '-';
    const aircraftType =
        project?.aircraftType || project?.leasingAircraft?.type || '-';

    const formattedCurrentDate = formatDate(currentDate);

    // Find specific components (engines, APU, landing gear)
    const findComponent = (namePattern: RegExp) => {
        return components.find((comp) =>
            namePattern.test(comp.name.toLowerCase()),
        );
    };

    return (
        <div className="border-b bg-muted/50 px-6 py-4">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-8">
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">
                            MSN
                        </div>
                        <div className="font-mono font-semibold text-foreground">
                            {msn}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">
                            Registry
                        </div>
                        <div className="font-mono font-semibold text-foreground">
                            {registration}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">
                            Delivery
                        </div>
                        <div className="font-semibold text-foreground">
                            {formatDate(project?.deliveryDate)}
                        </div>
                    </div>
                    {daysUntilDelivery !== null && (
                        <div>
                            <div className="font-semibold text-primary flex items-center gap-2">
                                <Clock className="h-4 w-4" />T
                                {daysUntilDelivery > 0 ? '-' : '+'}
                                {Math.abs(daysUntilDelivery)} days
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Left Column - Basic Info */}
                <div className="col-span-3 space-y-3">
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            CURRENT DATE:
                        </div>
                        <div className="font-semibold text-foreground">
                            {formattedCurrentDate}
                        </div>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            AIRCRAFT TYPE:
                        </div>
                        <div className="font-semibold text-foreground">
                            {aircraftType}
                        </div>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            TYPE OF AUDIT:
                        </div>
                        <div className="font-semibold text-foreground">
                            Transition{' '}
                            {/* #TODO: Replace hardcoded audit type with real project audit type */}
                        </div>
                    </div>
                </div>

                {/* Middle Left Column */}
                <div className="col-span-3 space-y-3">
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            MSN:
                        </div>
                        <div className="font-semibold text-foreground">
                            {msn}
                        </div>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            REGISTRATION:
                        </div>
                        <div className="font-semibold text-foreground">
                            {registration}
                        </div>
                    </div>
                    <div className="bg-muted rounded-lg p-3 flex gap-4">
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">
                                A/C Hours:
                            </div>
                            <div className="font-mono font-semibold text-foreground">
                                {project?.aircraft?.tsn || '-'}{' '}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">
                                A/C Cycles:
                            </div>
                            <div className="font-mono font-semibold text-foreground">
                                {project?.aircraft?.csn || '-'}{' '}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Middle Right Column - Milestones */}
                {/* #TODO: Replace all hardcoded milestone placeholders with real project milestone data */}
                <div className="col-span-3 space-y-3">
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            MPA RUN:
                        </div>
                        <div className="font-semibold text-foreground">-</div>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            DEMO FLIGHT:
                        </div>
                        <div className="font-semibold text-foreground">-</div>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                        <div className="text-xs text-muted-foreground mb-1">
                            BSI:
                        </div>
                        <div className="font-semibold text-foreground">-</div>
                    </div>
                </div>

                {/* Right Column - Components Table */}
                {/* #TODO: Replace all hardcoded component TSN/CSN values with real component data from backend */}
                <div className="col-span-3">
                    <div className="bg-card border rounded-lg p-3">
                        <div className="text-xs font-semibold text-card-foreground mb-2">
                            Components
                        </div>
                        <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-muted-foreground pb-1 border-b">
                                <div>Component</div>
                                <div className="text-right">TSN</div>
                                <div className="text-right">CSN</div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-card-foreground">
                                    ENG #1:
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    16,635:51
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    14,538
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-card-foreground">
                                    ENG #2:
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    15,486:48
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    13,691
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-card-foreground">APU:</div>
                                <div className="text-right font-mono text-card-foreground">
                                    14,657:00
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    17,864
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-card-foreground">NLG:</div>
                                <div className="text-right font-mono text-card-foreground">
                                    16,635:51
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    14,538
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-card-foreground">
                                    LHMLG:
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    16,635:51
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    14,538
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-card-foreground">
                                    RHMLG:
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    16,635:51
                                </div>
                                <div className="text-right font-mono text-card-foreground">
                                    14,538
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* #TODO: Replace hardcoded ferry flight and tech acceptance date placeholders with real project data */}
            <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="bg-muted rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">
                        FERRY FLIGHT:
                    </div>
                    <div className="font-semibold text-foreground">-</div>
                </div>
                <div className="bg-muted rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">
                        TECH ACCEPT DATE:
                    </div>
                    <div className="font-semibold text-foreground">-</div>
                </div>
            </div>
        </div>
    );
}
